import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { muscleName } from "../store.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { backLink } from "../components/common.js";
import { exerciseThumb, muscleBadges } from "../components/common.js";
import { spinner, errorState, toast, confirmDialog, openModal } from "../components/ui.js";
import { stars, starInput } from "../components/StarRating.js";
import { reportButton } from "../components/report.js";
import { relativeDay } from "../lib/format.js";

export default function PublicWorkout({ id }) {
  const root = h("div", { class: "screen" });
  let data = null;
  load();

  async function load() {
    setChildren(root, backLink(), spinner());
    try {
      data = await api.sharedWorkout(id);
      render();
    } catch (e) {
      setChildren(root, backLink(), errorState(e.message, () => navigate("/discover")));
    }
  }

  function render() {
    const author = data.owner.display_name || data.owner.username;
    setChildren(root,
      backLink(),
      h("div", { class: "card" },
        h("h1", { class: "detail-title" }, data.name),
        h("div", { class: "dim", style: { marginTop: "2px" } }, "by ", author, data.is_own ? " (you)" : ""),
        data.source_author ? h("div", { class: "dim hstack", style: { marginTop: "4px", gap: "4px", fontSize: ".8rem" } }, icon("copy", { size: 13 }), data.source_name ? `Adapted from ${data.source_name} by ${data.source_author}` : `Adapted from ${data.source_author}`) : null,
        data.description ? h("p", { style: { marginTop: "10px" } }, data.description) : null,
        h("div", { class: "hstack", style: { marginTop: "10px", gap: "14px", flexWrap: "wrap", fontSize: "0.85rem" } },
          data.rating_count
            ? h("span", { class: "hstack", style: { gap: "5px" } }, stars(data.avg_rating, { size: ".9rem" }), h("span", { class: "dim" }, `${data.avg_rating} · ${data.rating_count} rating${data.rating_count === 1 ? "" : "s"}`))
            : h("span", { class: "dim" }, "No ratings yet"),
          h("span", { class: "dim hstack", style: { gap: "4px" } }, icon("user", { size: 15 }), `${data.use_count} using`)
        )
      ),

      h("button", { class: "btn btn-primary btn-block btn-lg mt", onClick: adopt },
        icon(data.already_adopted ? "check" : "download", { size: 20 }),
        data.already_adopted ? "Add again to my workouts" : "Add to my workouts"
      ),

      data.can_rate ? ratingCard() : (data.is_own && data.is_public
        ? h("div", { class: "card mt center dim", style: { fontSize: ".85rem" } }, "This is your published workout — others can rate it from here.")
        : null),

      h("div", { class: "section-title" }, `Exercises (${data.exercises.length})`),
      h("div", { class: "card" }, ...data.exercises.map(exerciseRow)),

      data.reviews.length ? h("div", { class: "section-title" }, "Reviews") : null,
      data.reviews.length ? h("div", { class: "card" }, ...data.reviews.map(reviewRow)) : null,

      // Reporting is only meaningful for someone else's published content.
      !data.is_own ? h("div", { class: "center", style: { marginTop: "20px" } },
        reportButton({ targetType: "workout", targetId: Number(id), name: `${data.name} by ${author}` })) : null
    );
  }

  function exerciseRow(ex) {
    const setsLabel = `${ex.target_sets || 3} sets` + (ex.target_reps_min ? ` × ${ex.target_reps_min}${ex.target_reps_max && ex.target_reps_max !== ex.target_reps_min ? `–${ex.target_reps_max}` : ""}` : "");
    return h("div", { class: "row-between", style: { padding: "9px 0", alignItems: "center", gap: "10px" } },
      h("div", { class: "hstack", style: { gap: "10px", alignItems: "center" } },
        exerciseThumb(ex.images, { size: 42 }),
        h("div", null,
          h("div", { style: { fontWeight: 600 } }, ex.exercise_name),
          h("div", { class: "dim", style: { fontSize: ".82rem" } }, setsLabel)
        )
      ),
      muscleBadges((ex.primary_muscle_ids || []).slice(0, 1), [], muscleName)
    );
  }

  function reviewRow(r) {
    return h("div", { style: { padding: "10px 0", borderBottom: "1px solid var(--border)" } },
      h("div", { class: "row-between" },
        h("div", { style: { fontWeight: 600 } }, r.display_name || r.username),
        stars(r.rating, { size: ".8rem" })
      ),
      r.review ? h("p", { class: "dim", style: { marginTop: "4px", fontSize: ".88rem" } }, r.review) : null,
      h("div", { class: "dim", style: { fontSize: ".72rem", marginTop: "2px" } }, relativeDay(r.updated_at))
    );
  }

  function ratingCard() {
    const my = data.my_rating;
    // Tapping a star saves immediately (optimistic) — no extra confirmation step.
    return h("div", null,
      h("div", { class: "section-title" }, my ? "Your rating" : "Rate this workout"),
      h("div", { class: "card center" },
        starInput(my?.rating || 0, (v) => submitRating(v, data.my_rating?.review ?? null)),
        h("div", { class: "dim mt", style: { fontSize: ".82rem" } }, my ? "Tap a star to change your rating" : "Tap a star to rate"),
        my?.review ? h("p", { class: "mt", style: { fontStyle: "italic", fontSize: ".88rem" } }, `“${my.review}”`) : null,
        h("div", { class: "btn-row mt" },
          h("button", { class: "btn btn-ghost", onClick: openReviewModal }, icon("edit", { size: 16 }), my?.review ? "Edit review" : "Add a review"),
          my ? h("button", { class: "btn btn-ghost", onClick: removeRating }, icon("trash", { size: 16 }), "Remove") : null
        )
      )
    );
  }

  // Review editor — also lets you set the star rating (needed before a written review exists).
  function openReviewModal() {
    let rating = data.my_rating?.rating || 0;
    let review = data.my_rating?.review || "";
    const input = starInput(rating, (v) => (rating = v));
    const textarea = h("textarea", { class: "input", rows: 3, placeholder: "Add a short review (optional)", oninput: (e) => (review = e.target.value) }, review);
    const m = openModal({
      title: "Your review",
      content: h("div", null,
        h("div", { class: "center", style: { marginBottom: "12px" } }, input),
        textarea,
        h("div", { class: "btn-row mt" },
          h("button", { class: "btn btn-ghost", onClick: () => m.close() }, "Cancel"),
          h("button", { class: "btn btn-primary grow", onClick: async () => {
            if (!(rating >= 1)) { toast("Pick a star rating first", "warn"); return; }
            m.close();
            await submitRating(rating, review.trim() || null);
          } }, "Save")
        )
      ),
    });
  }

  async function submitRating(rating, review) {
    if (!(rating >= 1 && rating <= 5)) return;
    try {
      await api.rateWorkout(id, { rating, review });
      toast(`Rated ${rating}★`, "success");
      await load();
    } catch (e) {
      toast(e.message, "error");
    }
  }

  async function removeRating() {
    try {
      await api.unrateWorkout(id);
      toast("Rating removed", "success");
      await load();
    } catch (e) {
      toast(e.message, "error");
    }
  }

  async function adopt() {
    if (data.already_adopted) {
      const ok = await confirmDialog({ title: "Add again?", message: "You've already added this workout. Add another copy?", confirmText: "Add copy" });
      if (!ok) return;
    }
    try {
      const w = await api.copyWorkout(id);
      toast("Added to your workouts", "success");
      navigate(`/workouts/${w.id}/edit`);
    } catch (e) {
      toast(e.message, "error");
    }
  }

  return root;
}
